import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Twenty Tulips Diaries';
const DEFAULT_DESCRIPTION = 'Shop premium modest fashion, abaya cardigans, and elegant shawls at Twenty Tulips Diaries. Discover our curated collection of luxurious fabrics and timeless designs.';
const SITE_URL = 'https://twentytulipsdiaries.com';

export function SEO({ 
  title, 
  description = DEFAULT_DESCRIPTION, 
  image,
  type = 'website',
  pathname = '',
  noindex = false,
  product = null // For product pages
}) {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const url = `${SITE_URL}${pathname}`;
  const ogImage = image || `${SITE_URL}/og-image.jpg`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_SG" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Product-specific structured data */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": product.images,
            "brand": {
              "@type": "Brand",
              "name": SITE_NAME
            },
            "offers": {
              "@type": "Offer",
              "url": url,
              "priceCurrency": "SGD",
              "price": product.price,
              "availability": product.inventory > 0 
                ? "https://schema.org/InStock" 
                : "https://schema.org/OutOfStock"
            }
          })}
        </script>
      )}
    </Helmet>
  );
}

// Pre-configured SEO for common pages
export const PageSEO = {
  home: () => (
    <SEO 
      title="Modest Fashion & Abaya Cardigans"
      description="Shop premium modest fashion, abaya cardigans, and elegant shawls at Twenty Tulips Diaries. Discover our curated collection of luxurious fabrics and timeless designs crafted for every occasion."
      pathname="/"
    />
  ),
  
  shop: (collection) => (
    <SEO 
      title={collection && collection !== 'all' ? `${collection} Collection` : 'Shop All Products'}
      description={collection && collection !== 'all' 
        ? `Browse our ${collection} collection. Premium quality modest fashion and abaya cardigans at Twenty Tulips Diaries.`
        : 'Explore our complete collection of premium shawls, abaya cardigans, and modest fashion pieces. Free shipping on orders over $100.'
      }
      pathname="/shop"
    />
  ),
  
  collections: () => (
    <SEO 
      title="Collections"
      description="Discover our curated collections: Satin, Chiffon, Watercolor, and more. Each collection tells a unique story through fabric, color, and craftsmanship."
      pathname="/collections"
    />
  ),
  
  about: () => (
    <SEO 
      title="About Us"
      description="Learn about Twenty Tulips Diaries - your destination for premium quality shawls and modest fashion. We curate the finest collection crafted from luxurious materials."
      pathname="/about"
    />
  ),
  
  contact: () => (
    <SEO 
      title="Contact Us"
      description="Get in touch with Twenty Tulips Diaries. We'd love to hear from you. Contact us via email, phone, or Instagram for any inquiries about our products."
      pathname="/contact"
    />
  ),
  
  blog: () => (
    <SEO 
      title="Blog"
      description="Read the latest fashion tips, styling guides, and news from Twenty Tulips Diaries. Stay updated on modest fashion trends and new arrivals."
      pathname="/blog"
    />
  ),
  
  faq: () => (
    <SEO 
      title="Frequently Asked Questions"
      description="Find answers to common questions about Twenty Tulips Diaries products, shipping, returns, and more. Get the help you need quickly."
      pathname="/faq"
    />
  ),
  
  shipping: () => (
    <SEO 
      title="Shipping & Returns"
      description="Learn about our shipping policies and hassle-free returns at Twenty Tulips Diaries. Fast delivery and easy returns for your convenience."
      pathname="/shipping"
    />
  ),
  
  cart: () => (
    <SEO 
      title="Shopping Cart"
      description="Review your shopping cart at Twenty Tulips Diaries. Complete your purchase of premium modest fashion items."
      pathname="/cart"
      noindex={true}
    />
  ),
  
  login: () => (
    <SEO 
      title="Login"
      description="Sign in to your Twenty Tulips Diaries account to track orders, manage your profile, and enjoy exclusive member benefits."
      pathname="/login"
      noindex={true}
    />
  ),
  
  checkout: () => (
    <SEO 
      title="Checkout"
      description="Complete your purchase at Twenty Tulips Diaries."
      pathname="/checkout"
      noindex={true}
    />
  ),
  
  account: () => (
    <SEO 
      title="My Account"
      description="Manage your Twenty Tulips Diaries account, view orders, and update your profile."
      pathname="/account"
      noindex={true}
    />
  ),
  
  orders: () => (
    <SEO 
      title="My Orders"
      description="View your order history at Twenty Tulips Diaries."
      pathname="/orders"
      noindex={true}
    />
  ),
  
  terms: () => (
    <SEO 
      title="Terms of Service"
      description="Read the terms and conditions for using Twenty Tulips Diaries online store."
      pathname="/terms"
    />
  ),
  
  privacy: () => (
    <SEO 
      title="Privacy Policy"
      description="Learn how Twenty Tulips Diaries protects your personal information and data."
      pathname="/privacy"
    />
  )
};

export default SEO;
